// File: layouts/MainLayout.tsx

import { useRef, useEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import GlobalTopBar from "@/components/GlobalTopBar";
import LicenseBanner from "@/components/LicenseBanner";
import MobileNav from "@/components/MobileNav";
import { useSidebar } from "@/context/SidebarContext";
import { cn } from "@/lib/utils";

export default function MainLayout() {
    const { isInitialized, collapsed, toggle } = useSidebar();
    const topBarRef = useRef<HTMLDivElement>(null);
    const { pathname } = useLocation();
    const navigationType = useNavigationType();

    // A new page opens at its top, not at the last page's scroll position; Back keeps where it was
    useEffect(() => {
        if (navigationType !== "POP") window.scrollTo(0, 0);
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // Measure GlobalTopBar height and set as CSS variable
    // PatientDetailPage uses var(--topbar-height) for sticky positioning
    useEffect(() => {
        const updateHeight = () => {
            if (topBarRef.current) {
                const h = topBarRef.current.getBoundingClientRect().height;
                document.documentElement.style.setProperty("--topbar-height", `${h}px`);
            }
        };
        updateHeight();
        const ro = new ResizeObserver(updateHeight);
        if (topBarRef.current) ro.observe(topBarRef.current);
        return () => ro.disconnect();
    }, []);

    if (!isInitialized) {
        return (
            <div className="min-h-screen bg-background text-foreground">
                <div className="hidden sm:block fixed left-0 top-0 h-[100dvh] w-16 bg-[hsl(var(--sidebar-bg))] border-r border-border z-40" />
                <div className="sticky top-0 z-30 sm:ml-16" ref={topBarRef}>
                    <GlobalTopBar />
                    <LicenseBanner />
                </div>
                <div className="sm:ml-16">
                    <main className="bg-background">
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* App shell: full-height icon rail on the left (tablets and up; phones get the
                bottom tab bar), top bar and content to its right. */}
            <div className="hidden sm:block fixed left-0 top-0 h-[100dvh] z-40">
                <Sidebar />
            </div>

            {/* Below lg (tablets) the expanded rail overlays content; tap backdrop to close */}
            {!collapsed && (
                <div className="hidden sm:block fixed inset-0 z-30 bg-black/20 lg:hidden" onClick={toggle} />
            )}

            {/* From lg up the margin matches the rail's rendered width, so an expanded rail
                pushes the top bar and content instead of covering them */}
            <div className={cn(
                "sticky top-0 z-30",
                collapsed ? "sm:ml-16" : "sm:ml-16 lg:ml-52"
            )} ref={topBarRef}>
                <GlobalTopBar />
                <LicenseBanner />
            </div>

            <div className={collapsed ? "sm:ml-16" : "sm:ml-16 lg:ml-52"}>
                {/* Bottom padding keeps the last content clear of the phone tab bar */}
                <main className="bg-background p-0 pb-[var(--mobile-nav-height)]">
                    <Outlet />
                </main>
            </div>

            <MobileNav />
        </div>
    );
}